(() => {
  "use strict";

  class DigiClubProducts {
    constructor(section) {
      this.section = section;

      this.track =
        section.querySelector(
          "[data-digi-club-products-track]"
        );

      this.cards = [
        ...section.querySelectorAll(
          "[data-digi-club-products-card]"
        )
      ];

      this.filters = [
        ...section.querySelectorAll(
          "[data-digi-club-products-filter]"
        )
      ];

      this.previousButton =
        section.querySelector(
          "[data-digi-club-products-previous]"
        );

      this.nextButton =
        section.querySelector(
          "[data-digi-club-products-next]"
        );

      this.dots = [
        ...section.querySelectorAll(
          "[data-digi-club-products-dot]"
        )
      ];

      this.noResults =
        section.querySelector(
          "[data-digi-club-products-no-results]"
        );

      this.revealItems = [
        ...section.querySelectorAll(
          "[data-digi-club-products-reveal]"
        )
      ];

      this.animationEnabled =
        section.dataset.animationEnabled === "true";

      this.mobileLayout =
        section.dataset.mobileLayout || "carousel";

      this.reduceMotionQuery =
        window.matchMedia(
          "(prefers-reduced-motion: reduce)"
        );

      this.activeFilter = "all";
      this.visibleCards = [...this.cards];
      this.currentIndex = 0;

      this.scrollTimer = null;
      this.resizeTimer = null;
      this.observer = null;

      this.handleScroll =
        this.handleScroll.bind(this);

      this.handleResize =
        this.handleResize.bind(this);

      this.handlePrevious =
        this.handlePrevious.bind(this);

      this.handleNext =
        this.handleNext.bind(this);

      this.handleKeydown =
        this.handleKeydown.bind(this);

      this.handleReducedMotion =
        this.handleReducedMotion.bind(this);

      this.init();
    }

    init() {
      if (!this.track || !this.cards.length) {
        this.showAll();
        return;
      }

      this.bindEvents();
      this.initializeReveal();

      this.applyFilter(
        this.getInitialFilter()
      );
    }

    bindEvents() {
      this.track.addEventListener(
        "scroll",
        this.handleScroll,
        { passive: true }
      );

      this.track.addEventListener(
        "keydown",
        this.handleKeydown
      );

      window.addEventListener(
        "resize",
        this.handleResize,
        { passive: true }
      );

      this.previousButton?.addEventListener(
        "click",
        this.handlePrevious
      );

      this.nextButton?.addEventListener(
        "click",
        this.handleNext
      );

      this.filters.forEach(filter => {
        filter.addEventListener(
          "click",
          () => {
            const value =
              filter.dataset.filterValue || "all";

            this.applyFilter(value);
          }
        );
      });

      this.dots.forEach(dot => {
        dot.addEventListener(
          "click",
          () => {
            const blockId =
              dot.dataset.productBlockId;

            const index =
              this.visibleCards.findIndex(
                card =>
                  card.dataset.productBlockId ===
                  blockId
              );

            if (index < 0) return;

            this.scrollToIndex(index);
          }
        );
      });

      if (
        typeof this.reduceMotionQuery.addEventListener ===
        "function"
      ) {
        this.reduceMotionQuery.addEventListener(
          "change",
          this.handleReducedMotion
        );
      } else if (
        typeof this.reduceMotionQuery.addListener ===
        "function"
      ) {
        this.reduceMotionQuery.addListener(
          this.handleReducedMotion
        );
      }
    }

    getInitialFilter() {
      const activeFilter =
        this.filters.find(filter =>
          filter.classList.contains("is-active")
        );

      return (
        activeFilter?.dataset.filterValue ||
        "all"
      );
    }

    isMobileCarousel() {
    return (
        window.innerWidth < 990 &&
        this.mobileLayout === "carousel"
    );
    }

    isDesktopCarousel() {
    return (
        window.innerWidth >= 990 &&
        this.visibleCards.length > 4
    );
    }

    isCarouselMode() {
    return (
        this.isMobileCarousel() ||
        this.isDesktopCarousel()
    );
    }

    updateLayoutMode() {
    const desktopCarousel =
        this.isDesktopCarousel();

    this.section.classList.toggle(
        "is-desktop-carousel",
        desktopCarousel
    );

    this.section.classList.toggle(
        "is-static-grid",
        !desktopCarousel
    );
    }

    initializeReveal() {
      if (
        !this.animationEnabled ||
        this.reduceMotionQuery.matches
      ) {
        this.showAll();
        return;
      }

      const items = [
        ...this.revealItems,
        ...this.cards
      ];

      if (!items.length) return;

      this.observer =
        new IntersectionObserver(
          entries => {
            entries.forEach(entry => {
              if (!entry.isIntersecting) {
                return;
              }

              entry.target.classList.add(
                "is-visible"
              );

              this.observer?.unobserve(
                entry.target
              );
            });
          },
          {
            threshold:0.12,
            rootMargin:"0px 0px -8% 0px"
          }
        );

      items.forEach(item => {
        this.observer.observe(item);
      });
    }

    applyFilter(value) {
    this.activeFilter =
        value || "all";

    this.updateFilterButtons();
    this.updateCards();
    this.updateVisibleCards();

    /*
        Verifica se a categoria atual possui
        mais de quatro produtos no desktop.
    */
    this.updateLayoutMode();

    this.updateDotsVisibility();
    this.resetCarouselPosition();
    this.updateNavigation();
    this.updateNoResults();
    }

    updateFilterButtons() {
      this.filters.forEach(filter => {
        const filterValue =
          filter.dataset.filterValue || "all";

        const isActive =
          filterValue === this.activeFilter;

        filter.classList.toggle(
          "is-active",
          isActive
        );

        filter.setAttribute(
          "aria-pressed",
          isActive
            ? "true"
            : "false"
        );
      });
    }

    updateCards() {
      this.cards.forEach(card => {
        const cardFilter =
          card.dataset.productFilter || "all";

        const shouldShow =
          this.activeFilter === "all" ||
          cardFilter === this.activeFilter;

        card.classList.toggle(
          "is-filtered-out",
          !shouldShow
        );

        card.setAttribute(
          "aria-hidden",
          shouldShow
            ? "false"
            : "true"
        );

        if (shouldShow) {
          card.classList.add(
            "is-visible"
          );
        }
      });
    }

    updateVisibleCards() {
      this.visibleCards =
        this.cards.filter(
          card =>
            !card.classList.contains(
              "is-filtered-out"
            )
        );

      this.currentIndex =
        this.clamp(
          this.currentIndex,
          0,
          Math.max(
            this.visibleCards.length - 1,
            0
          )
        );
    }

    updateDotsVisibility() {
      this.dots.forEach(dot => {
        const blockId =
          dot.dataset.productBlockId;

        const correspondingCard =
          this.cards.find(
            card =>
              card.dataset.productBlockId ===
              blockId
          );

        const isVisible =
          correspondingCard &&
          !correspondingCard.classList.contains(
            "is-filtered-out"
          );

        dot.classList.toggle(
          "is-hidden",
          !isVisible
        );

        dot.disabled =
          !isVisible;
      });

      this.updateDots();
    }

    updateNoResults() {
      if (!this.noResults) return;

      this.noResults.hidden =
        this.visibleCards.length > 0;
    }

    resetCarouselPosition() {
      this.currentIndex = 0;

      if (!this.track) {
        return;
      }

      const targetCard =
        this.visibleCards[0];

      if (!targetCard) {
        this.updateDots();
        return;
      }

      const targetLeft =
        targetCard.offsetLeft -
        this.track.offsetLeft;

      this.track.scrollTo({
        left: targetLeft,
        behavior: this.reduceMotionQuery.matches
          ? "auto"
          : "smooth"
      });

      this.updateDots();
    }

    handleScroll() {
      if (!this.isCarouselMode()) return;

      window.clearTimeout(
        this.scrollTimer
      );

      this.scrollTimer =
        window.setTimeout(() => {
          this.updateActiveIndex();
          this.updateNavigation();
        }, 70);
    }

    handleResize() {
    window.clearTimeout(
        this.resizeTimer
    );

    this.resizeTimer =
        window.setTimeout(() => {
        this.updateVisibleCards();
        this.updateLayoutMode();
        this.updateActiveIndex();
        this.updateNavigation();
        }, 100);
    }

    handlePrevious() {
      if (!this.isCarouselMode()) return;

      this.scrollToIndex(
        this.currentIndex - 1
      );
    }

    handleNext() {
      if (!this.isCarouselMode()) return;

      this.scrollToIndex(
        this.currentIndex + 1
      );
    }

    handleKeydown(event) {
      if (!this.isCarouselMode()) return;

      if (event.key === "ArrowLeft") {
        event.preventDefault();
        this.handlePrevious();
      }

      if (event.key === "ArrowRight") {
        event.preventDefault();
        this.handleNext();
      }

      if (event.key === "Home") {
        event.preventDefault();
        this.scrollToIndex(0);
      }

      if (event.key === "End") {
        event.preventDefault();

        this.scrollToIndex(
          this.visibleCards.length - 1
        );
      }
    }

    handleReducedMotion(event) {
      if (!event.matches) return;

      this.destroyObserver();
      this.showAll();
    }

    scrollToIndex(index) {
      if (!this.visibleCards.length) return;

    const targetIndex =
    this.clamp(
        index,
        0,
        this.getMaximumIndex()
    );

      const targetCard =
        this.visibleCards[targetIndex];

      if (!targetCard) return;

      this.track.scrollTo({
        left:targetCard.offsetLeft,
        behavior:
          this.reduceMotionQuery.matches
            ? "auto"
            : "smooth"
      });

      this.currentIndex =
        targetIndex;

      this.updateDots();
      this.updateNavigation();
    }

    updateActiveIndex() {
    if (
        !this.isCarouselMode() ||
        !this.visibleCards.length
    ) {
        this.currentIndex = 0;

        this.updateDots();
        this.updateNavigation();

        return;
    }

    const trackScrollLeft =
        this.track.scrollLeft;

    const trackStart =
        this.track.offsetLeft;

    let closestIndex = 0;
    let closestDistance = Infinity;

    this.visibleCards.forEach(
        (card, index) => {
        const cardPosition =
            card.offsetLeft -
            trackStart;

        const distance =
            Math.abs(
            cardPosition -
            trackScrollLeft
            );

        if (distance < closestDistance) {
            closestDistance =
            distance;

            closestIndex =
            index;
        }
        }
    );

    this.currentIndex =
        closestIndex;

    this.updateDots();
    }

    updateDots() {
      const activeCard =
        this.visibleCards[
          this.currentIndex
        ];

      const activeBlockId =
        activeCard?.dataset
          .productBlockId;

      this.dots.forEach(dot => {
        const isHidden =
          dot.classList.contains(
            "is-hidden"
          );

        const isActive =
          !isHidden &&
          dot.dataset.productBlockId ===
            activeBlockId;

        dot.classList.toggle(
          "is-active",
          isActive
        );

        dot.setAttribute(
          "aria-current",
          isActive
            ? "true"
            : "false"
        );
      });
    }

    updateNavigation() {
      if (
        !this.previousButton ||
        !this.nextButton
      ) {
        return;
      }

      const carouselActive =
        this.isCarouselMode();

      const hasMultipleCards =
        this.visibleCards.length > 1;

      this.previousButton.disabled =
        !carouselActive ||
        !hasMultipleCards ||
        this.currentIndex <= 0;

        this.nextButton.disabled =
        !carouselActive ||
        !hasMultipleCards ||
        this.currentIndex >=
            this.getMaximumIndex();
    }

    getVisibleCardsPerView() {
    if (this.isDesktopCarousel()) {
        return 4;
    }

    return 1;
    }

    getMaximumIndex() {
    const cardsPerView =
        this.getVisibleCardsPerView();

    return Math.max(
        this.visibleCards.length -
        cardsPerView,
        0
    );
    }

    showAll() {
      [
        ...this.revealItems,
        ...this.cards
      ].forEach(item => {
        item.classList.add(
          "is-visible"
        );
      });
    }

    clamp(value,minimum,maximum) {
      return Math.min(
        Math.max(value,minimum),
        maximum
      );
    }

    destroyObserver() {
      if (!this.observer) return;

      this.observer.disconnect();
      this.observer = null;
    }

    destroy() {
      this.destroyObserver();

      window.clearTimeout(
        this.scrollTimer
      );

      window.clearTimeout(
        this.resizeTimer
      );

      this.track?.removeEventListener(
        "scroll",
        this.handleScroll
      );

      this.track?.removeEventListener(
        "keydown",
        this.handleKeydown
      );

      window.removeEventListener(
        "resize",
        this.handleResize
      );

      this.previousButton?.removeEventListener(
        "click",
        this.handlePrevious
      );

      this.nextButton?.removeEventListener(
        "click",
        this.handleNext
      );

      if (
        typeof this.reduceMotionQuery.removeEventListener ===
        "function"
      ) {
        this.reduceMotionQuery.removeEventListener(
          "change",
          this.handleReducedMotion
        );
      } else if (
        typeof this.reduceMotionQuery.removeListener ===
        "function"
      ) {
        this.reduceMotionQuery.removeListener(
          this.handleReducedMotion
        );
      }
    }
  }

  const initializeSections = root => {
    root
      .querySelectorAll(
        "[data-digi-club-products]"
      )
      .forEach(section => {
        if (
          section.dataset
            .digiClubProductsInitialized ===
          "true"
        ) {
          return;
        }

        section.dataset
          .digiClubProductsInitialized =
          "true";

        section.__digiClubProducts =
          new DigiClubProducts(section);
      });
  };

  const unloadSection = section => {
    const productsSection =
      section.matches?.(
        "[data-digi-club-products]"
      )
        ? section
        : section.querySelector?.(
            "[data-digi-club-products]"
          );

    if (!productsSection) return;

    productsSection
      .__digiClubProducts
      ?.destroy();

    delete productsSection
      .__digiClubProducts;

    delete productsSection.dataset
      .digiClubProductsInitialized;
  };

  if (document.readyState === "loading") {
    document.addEventListener(
      "DOMContentLoaded",
      () => {
        initializeSections(document);
      }
    );
  } else {
    initializeSections(document);
  }

  document.addEventListener(
    "shopify:section:load",
    event => {
      initializeSections(
        event.target
      );
    }
  );

  document.addEventListener(
    "shopify:section:unload",
    event => {
      unloadSection(
        event.target
      );
    }
  );

  document.addEventListener(
    "shopify:block:select",
    event => {
      const card =
        event.target.closest?.(
          "[data-digi-club-products-card]"
        );

      if (!card) return;

      card.classList.add(
        "is-visible"
      );

      const section =
        card.closest(
          "[data-digi-club-products]"
        );

      const instance =
        section?.__digiClubProducts;

      if (!instance) return;

      const cardFilter =
        card.dataset.productFilter ||
        "all";

      if (
        card.classList.contains(
          "is-filtered-out"
        )
      ) {
        const matchingFilter =
          instance.filters.find(
            filter =>
              filter.dataset.filterValue ===
              cardFilter
          );

        if (matchingFilter) {
          instance.applyFilter(
            cardFilter
          );
        } else {
          instance.applyFilter(
            "all"
          );
        }
      }

      const index =
        instance.visibleCards.indexOf(
          card
        );

      if (
        index >= 0 &&
        instance.isCarouselMode()
      ) {
        instance.scrollToIndex(index);
      }
    }
  );
})();